import { gql } from '@graphql/__generated__';

// Compact activity shape reused by the dashboard buyer-leads list and the
// "Needs attention" triage block (next scheduled / last completed activity).
export const BUYER_LEAD_DASHBOARD_ACTIVITY_FRAGMENT = gql(/* GraphQL */ `
  fragment BuyerLeadDashboardActivity on activities {
    id
    activity_type
    created_at
    start_date
    due_at
    done
    done_at
    success
    subject
    note
    consolidated_due_at
    consolidated_done_at
  }
`);

export const BUYER_LEAD_DASHBOARD_ROW_FRAGMENT = gql(/* GraphQL */ `
  fragment BuyerLeadDashboardRow on buyer_leads {
    id
    source
    stage_id
    created_at
    updated_at
    lead_stage {
      id
      name
      label
      order_nr
    }
    user {
      id
      ...UserCard_user
    }
    mls_user {
      id
      first_name
      last_name
      full_name
      primary_email
      primary_phone_number
      profile_image_url
    }
    next_activity: activities(
      where: {
        done: { _eq: false }
        activity_type: { _in: [call, visit, task, assignment] }
      }
      order_by: { consolidated_due_at: asc_nulls_last }
      limit: 1
    ) {
      ...BuyerLeadDashboardActivity
    }
    last_activity: activities(
      where: {
        done: { _eq: true }
        activity_type: { _in: [call, visit, note, assignment, task] }
        consolidated_done_at: { _is_null: false }
      }
      order_by: { consolidated_done_at: desc_nulls_last }
      limit: 1
    ) {
      ...BuyerLeadDashboardActivity
    }
  }
`);

export const GET_LISTING_BUYERS_DASHBOARD = gql(/* GraphQL */ `
  query GetListingBuyersDashboard($id: uuid!) {
    lots_by_pk(id: $id) {
      id
      enable_auto_matching
      offers {
        id
        status
      }
      buyer_leads(order_by: { updated_at: desc }) {
        ...BuyerLeadDashboardRow
      }
      enquiries(where: { processed: { _eq: false } }) {
        id
        buyer_lead_id
      }
    }
    # Viewings = visit activities on buyer leads of this listing. Scheduled
    # visits set buyer_lead_id (not the activity's own lot_id), so scope via the
    # buyer_lead relationship and split by done state for the per-status counts.
    viewings_pending: activities_aggregate(
      where: {
        buyer_lead: { lot_id: { _eq: $id } }
        activity_type: { _eq: visit }
        done: { _eq: false }
      }
    ) {
      aggregate {
        count
      }
    }
    viewings_done: activities_aggregate(
      where: {
        buyer_lead: { lot_id: { _eq: $id } }
        activity_type: { _eq: visit }
        done: { _eq: true }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`);

// Drives the dashboard "Buyer leads" list when quick filters are applied. The
// `where` already scopes to the lot; the other dashboard cards keep using the
// full set from GET_LISTING_BUYERS_DASHBOARD so they don't react to filtering.
export const GET_LISTING_BUYER_LEADS_LIST = gql(/* GraphQL */ `
  query GetListingBuyerLeadsList($where: buyer_leads_bool_exp!) {
    buyer_leads(where: $where, order_by: { updated_at: desc }) {
      ...BuyerLeadDashboardRow
    }
  }
`);

// One sent mass-email, reached through its `activities` row (which links the
// message to the buyer lead). `email_messages`/`email_campaigns` aren't
// root-queryable for CRM roles, but the relationship from `activities` is — so
// we group these client-side by `email_message.campaign_id` into batches.
export const LISTING_EMAIL_CAMPAIGN_ACTIVITY_FRAGMENT = gql(/* GraphQL */ `
  fragment ListingEmailCampaignActivity on activities {
    id
    created_at
    buyer_lead {
      id
      user {
        id
        ...UserCard_user
      }
      mls_user {
        id
        first_name
        last_name
        full_name
        primary_email
        primary_phone_number
        profile_image_url
      }
    }
    email_message {
      id
      campaign_id
      status
      subject
      created_at
      error_message
      to
    }
  }
`);

// Lightweight mass-email summaries for a listing. Recipient details are loaded
// only when a campaign is opened.
export const GET_LISTING_EMAIL_CAMPAIGNS = gql(/* GraphQL */ `
  query GetListingEmailCampaigns($lotId: uuid!) {
    activities(
      where: {
        activity_type: { _eq: email }
        buyer_lead: { lot_id: { _eq: $lotId } }
        email_message: { campaign_id: { _is_null: false } }
      }
      order_by: { created_at: desc }
    ) {
      email_message {
        id
        campaign_id
        status
        subject
        created_at
      }
    }
  }
`);

export const GET_LISTING_EMAIL_CAMPAIGN_RECIPIENTS = gql(/* GraphQL */ `
  query GetListingEmailCampaignRecipients($lotId: uuid!, $campaignId: uuid!) {
    activities(
      where: {
        activity_type: { _eq: email }
        buyer_lead: { lot_id: { _eq: $lotId } }
        email_message: { campaign_id: { _eq: $campaignId } }
      }
      order_by: { created_at: asc }
    ) {
      ...ListingEmailCampaignActivity
    }
  }
`);
